import type {
  Conversation,
  ConversationRecap,
  ConversationStartRequest,
  ConversationSummary,
  ConversationTurn,
  ConversationTurnRequest,
  ConversationTurnResponse,
  Correction,
  CorrectionAction,
  CorrectionActionRequest,
  Page,
  Uuid,
} from '@nelnela/contracts';
import { dropOverlaps, reanchor } from '@nelnela/corrections';
import { ApiError } from './api';
import type { Placement } from './journal';
import { tokenize } from './text';

/**
 * The chat's pure half (M6c step 11).
 *
 * `screens/Chat.tsx` is a scenario drawer, a scroll of bubbles, three chips and a composer. Only
 * four of those need a browser — the scroll-to-bottom, the composer's `selectionStart`, the
 * auto-growing textarea and `visualViewport`. Everything else is a function of (turns,
 * corrections, what the learner has acted on), and it lives here because the web suite runs in
 * node with no DOM: a rule that stays in the component is a rule no test in this repo can reach.
 *
 * **Nothing here re-implements span logic, and nothing here re-implements the journal.**
 * `reanchor` and `dropOverlaps` come from `@nelnela/corrections` — the worker module aliased into
 * this bundle — and `Placement`, `insertSuggestion` and `editedAgo` come from `lib/journal.ts`,
 * where M6c-i built them. The standing warning is M6a's dictation tint, where a punctuation class
 * copied into a second file drifted by ONE code point and silently killed a feature for a whole
 * milestone. The one thing this module does NOT reuse is `placementsFor`, and the reason is a
 * genuine difference in policy rather than convenience — see `turnPlacements`.
 */

// ---------------------------------------------------------------------------------------------
// the scenarios
// ---------------------------------------------------------------------------------------------

export interface ScenarioRow {
  /** matches an `id` in the worker's `SCENARIOS`, so the server resolves it to a full setup */
  id: string;
  /** the design's own words, `12e` row for row */
  label: string;
}

/**
 * The three scenarios design `12e` draws, plus the free-text row the picker adds itself.
 *
 * **These are a copy, and the copy is deliberate — but it is pinned.** The authority is
 * `SCENARIOS` in `packages/worker/src/lib/prompts/chat.ts`, which holds twelve entries with the
 * `setup` paragraph each one means. That module cannot be aliased into this bundle the way
 * `@nelnela/corrections` and `@nelnela/tokenize` are: `aliases.ts` states the rule those three
 * entries obey — "every entry points at a zero-import module in the worker package" — and the
 * chat prompt imports `zod`, `../jobs` and `../llm`, so aliasing it would pull the Anthropic
 * transport into a browser bundle to render three strings.
 *
 * The alternative that needs no copy is a route that serves the catalogue, and it was rejected
 * for this milestone: it is a contract addition, a worker route and a network round trip before a
 * sheet can open, all to avoid three literals whose drift a test can catch for free. So
 * `chat.test.ts` reads the worker's source and asserts these three ids and labels are its first
 * three, byte for byte — the same two-tier pin the `ERROR_TAGS` ↔ migration cross-check uses.
 *
 * If the sheet should ever offer all twelve, the durable fix is to split `SCENARIOS` out of the
 * prompt module into a zero-import file and alias THAT; the wire type does not change either way,
 * because `ConversationStartRequest.scenario` is free text.
 */
export const SCENARIO_ROWS: readonly ScenarioRow[] = [
  { id: 'texting_friend', label: 'texting a friend about plans' },
  { id: 'at_the_shop', label: 'at the shop' },
  { id: 'host_family_dinner', label: 'at dinner with the host family' },
];

const SCENARIO_LABEL = new Map(SCENARIO_ROWS.map((s) => [s.id, s.label]));

/**
 * What the header calls this conversation.
 *
 * A lookup with a fallback, exactly as the server's `scenarioSetup` is: an id we know becomes its
 * label, and anything else IS the label — that is what "or describe your own…" means. A free-text
 * scenario is capped for the header only; the whole of it is what the model was told.
 */
export const SCENARIO_TITLE_MAX = 44;

export function scenarioLabel(scenario: string): string {
  const known = SCENARIO_LABEL.get(scenario);
  if (known) return known;
  const own = scenario.trim();
  if (own.length === 0) return 'text chat';
  return own.length > SCENARIO_TITLE_MAX ? `${own.slice(0, SCENARIO_TITLE_MAX).trimEnd()}…` : own;
}

// ---------------------------------------------------------------------------------------------
// what the composer is allowed to do
// ---------------------------------------------------------------------------------------------

/**
 * The client's copies of two server caps. Both err the safe way — the client stops before the
 * server would, never after — which is `lib/journal.ts`'s stated rule for `CHECK_CAP_PER_DAY` and
 * the same reasoning: sharing the constants would mean aliasing the worker's `budget.ts` and
 * `validate/schemas.ts`, which import the database layer, into a browser bundle for two numbers.
 */
export const TURN_MAX_CHARS = 2_000;
/**
 * `MAX_TURNS_PER_CONVERSATION` in `packages/worker/src/lib/budget.ts`.
 *
 * **Counted over the LEARNER's turns only, and the comment that used to stand here was wrong about
 * that.** It read: "counted here over BOTH sides' turns … if the server in fact counts only the
 * learner's, this client stops at twenty exchanges instead of forty — early, calm and with the
 * conversation intact, which is the direction a copied cap is allowed to be wrong in." The server
 * does count only the learner's (`countLearnerTurns` in `routes/conversations.ts` —
 * `WHERE speaker = 'learner'`), so the client was killing Send at learner turn 21 of 40 — and the
 * stop was neither early-but-calm nor intact: `shut` stayed false, so the composer kept its live
 * `write in Georgian…` placeholder, the Send button was merely dimmed, Enter was swallowed, and no
 * copy anywhere said why. The learner typed into a dead end they could only leave via End or Back.
 *
 * Both halves are fixed together, because either alone leaves the other: the count now matches the
 * server's, and a refusal now carries a NAMED reason (`SendBlock`) that the screen prints.
 */
export const MAX_TURNS = 40;

/** The count the cap is against, in the client's words: `WHERE speaker = 'learner'`. */
export function learnerTurnCount(turns: readonly Pick<ConversationTurn, 'speaker'>[]): number {
  return turns.reduce((n, t) => (t.speaker === 'learner' ? n + 1 : n), 0);
}

/**
 * The run's one `chat` activity, from the screen's point of view.
 *
 * A turn cannot be sent without one — every turn's cost is ledgered against it and every
 * `production_use` event hangs off it — so the composer has to know which of the three states it
 * is in. It used to know none of them: `send` read `activityRef.current?.id` and returned
 * silently when it was missing, which made a permanent `startActivity` failure into a chat where
 * Send, Enter and every reply chip did literally nothing, forever, with the button un-dimmed.
 */
export type ActivityState = 'starting' | 'ready' | 'failed';

/**
 * Why a send was refused, when it was refused by the client rather than by the server.
 *
 * A boolean was the bug in both halves of this section: `canSend` said no and the screen had
 * nothing to say about it. These are the sentences instead — `sendBlockLine` for what the screen
 * says, `sendBlockPlaceholder` for what the box says when the block is not going to clear itself.
 */
export type SendBlock =
  | 'ended'
  | 'turn_cap'
  | 'activity_failed'
  | 'activity_starting'
  | 'in_flight'
  | 'empty'
  | 'too_long';

export interface SendGate {
  draft: string;
  /** a turn is already in flight — a second send would race it and pay for two replies */
  inFlight: boolean;
  /** the LEARNER's turns stored so far, NOT both speakers — see `MAX_TURNS` */
  learnerTurns: number;
  ended: boolean;
  activity: ActivityState;
}

/**
 * The first reason this send cannot go, or `null`.
 *
 * **The order is what makes the standing reasons visible.** `ended`, `turn_cap` and a broken
 * activity are checked BEFORE `empty`, so a learner staring at an empty box gets told the
 * conversation has run long rather than nothing at all — which is precisely how the cap stayed
 * invisible: nothing was ever sent, so nothing ever failed, so nothing was ever said.
 */
export function sendBlock(g: SendGate): SendBlock | null {
  if (g.ended) return 'ended';
  if (g.learnerTurns >= MAX_TURNS) return 'turn_cap';
  if (g.activity === 'failed') return 'activity_failed';
  if (g.activity === 'starting') return 'activity_starting';
  if (g.inFlight) return 'in_flight';
  const draft = g.draft.trim();
  if (draft.length === 0) return 'empty';
  if (draft.length > TURN_MAX_CHARS) return 'too_long';
  return null;
}

/**
 * The same question as a yes/no, for the Send button's `disabled` and for the tests that read
 * better in it. `sendBlock` is the one the screen uses, because a button that is merely dimmed is
 * what this section was fixed for.
 */
export function canSend(g: SendGate): boolean {
  return sendBlock(g) === null;
}

/**
 * The calm line for a refusal the client made itself, in `chatCalmLine`'s register: say what is
 * still true, and never apologise. `null` where a line would be noise — an empty box explains
 * itself, and a turn in flight is already drawn as a shimmer.
 *
 * `ended` gets none for the same reason: the bar says `ended`, and the placeholder says it again.
 */
export function sendBlockLine(block: SendBlock): string | null {
  switch (block) {
    case 'turn_cap':
      // The server's own sentence, near enough word for word (`conversations.ts`), so the two
      // agree on the day a race puts the server's 429 on screen instead of this.
      return `That is ${MAX_TURNS} turns — end it to see the recap, and start another whenever you like.`;
    case 'activity_failed':
      return 'This conversation can’t take turns right now. Go back and open it again — everything you have said is saved.';
    case 'activity_starting':
      // The routine race: the activity POST is a round trip, so a send in the first few hundred ms
      // after mount used to be swallowed without a trace.
      return 'One moment — the conversation is still opening.';
    case 'too_long':
      return `That message is longer than one turn can carry — about ${TURN_MAX_CHARS} characters is the limit.`;
    case 'ended':
    case 'in_flight':
    case 'empty':
      return null;
  }
}

/**
 * What the composer says instead of `write in Georgian…` while a block is STANDING — one the
 * learner is stuck behind until they do something about it, as opposed to one that clears itself
 * (a turn in flight, an empty box, an activity still opening).
 *
 * `ended` is absent on purpose: the screen has its own sentence for it and had it before this.
 */
export function sendBlockPlaceholder(block: SendBlock | null): string | undefined {
  switch (block) {
    case 'turn_cap':
      return 'this conversation has run long — End it to see the recap';
    case 'activity_failed':
      return 'this conversation can’t take turns right now';
    default:
      return undefined;
  }
}

/**
 * The block if it is standing, else `null` — the screen's cue to disable the box and print the
 * line without waiting for the learner to press something and discover it.
 *
 * Defined as "has a placeholder" rather than as a second list, so the two can never disagree.
 */
export function standingBlock(block: SendBlock | null): SendBlock | null {
  return block !== null && sendBlockPlaceholder(block) !== undefined ? block : null;
}

/**
 * A send in flight, or the last one that failed: the text, the id it goes under, and whether the
 * app handed it over.
 *
 * `eventId` was already kept across a retry, because `conversationTurn` is idempotent on it and a
 * retry of a request that actually landed would otherwise produce two exchanges. `fromChip` was
 * NOT, and that was the bug: a chip send that failed put the chip's own text back in the composer,
 * and re-sending it with the Send button passed `fromChip: false`. The server's replay check finds
 * nothing (the original never landed), writes a brand-new learner turn with `suggested_taken`
 * unset, and its `production_use` events carry no `support.hintUsed` — so the grader awards `easy`
 * on produce plus the read echo it explicitly forbids for a hinted use, for a sentence the learner
 * did not compose. One dropped request defeated the whole `hintUsed` chain.
 */
export interface PendingSend {
  /** the trimmed draft, exactly as it was sent */
  text: string;
  eventId: string;
  /** the text came from a suggested-reply chip: recognition, not production */
  fromChip: boolean;
}

/**
 * The identity this send goes under: the retry's id and hint flag when the text is the same,
 * a fresh one otherwise.
 *
 * `||` rather than "the stored value wins", because the pair runs both ways. A chip that failed
 * and is re-sent from the box is still a chip; a typed sentence that failed and is then sent by
 * TAPPING a chip with the same words is also a chip. The flag can only be turned on by a route
 * that really was hinted, and never turned off by a stale entry.
 */
export function sendIdentity(
  pending: PendingSend | null,
  draft: string,
  fromChip: boolean,
  freshEventId: string,
): PendingSend {
  if (pending && pending.text === draft) {
    return { text: draft, eventId: pending.eventId, fromChip: pending.fromChip || fromChip };
  }
  return { text: draft, eventId: freshEventId, fromChip };
}

/** The three chips: whatever the most recent app turn offered, and nothing once one is taken. */
export function latestSuggestions(turns: readonly ConversationTurn[]): string[] {
  for (let i = turns.length - 1; i >= 0; i--) {
    const t = turns[i]!;
    if (t.speaker !== 'app') continue;
    return t.suggestedReplies ?? [];
  }
  return [];
}

// ---------------------------------------------------------------------------------------------
// the optimistic turn
// ---------------------------------------------------------------------------------------------

/**
 * The id the learner's own bubble wears while its turn is in flight.
 *
 * Not a real ULID or uuid and deliberately not one: it is a React key and a "is this the pending
 * one" test, and a value that could be mistaken for a server id is a value that will eventually
 * be sent to the server by accident.
 */
export const PENDING_TURN_ID = 'pending' as Uuid;

/**
 * The learner's message, rendered before the server has seen it.
 *
 * `ConversationTurnResponse` returns BOTH turns in one response and there is no streaming, so a
 * turn takes as long as a Haiku call — several seconds during which a chat app that showed
 * nothing would look broken. The learner's own words go up the moment they press send, and the
 * app's side of the exchange is a shimmer, never a spinner (the rule M6b applied to an unwarmed
 * piece: say what is coming, do not draw a wheel).
 *
 * It is a whole `ConversationTurn` rather than a bare string so the bubble component takes one
 * type. `audio: null` is honest here for two reasons at once: a learner turn never has app audio,
 * and text chat has none at all.
 */
export function optimisticLearnerTurn(text: string, suggestedTaken: boolean): ConversationTurn {
  return {
    id: PENDING_TURN_ID,
    idx: -1,
    speaker: 'learner',
    text,
    audio: null,
    suggestedTaken,
  };
}

// ---------------------------------------------------------------------------------------------
// corrections on your own bubble
// ---------------------------------------------------------------------------------------------

/**
 * Where a turn's corrections sit in it, and which may still be tapped.
 *
 * **A sent turn is immutable, and that is the whole difference from the journal.** The offsets
 * stored against a `conversation_turn` are offsets into text that can never change, so `reanchor`
 * is a no-op here in the normal case — it is used anyway, rather than trusting the numbers,
 * because it costs nothing and because it is the same function that produced them. If a stored
 * offset is ever wrong, this finds the quote again instead of underlining a word at random.
 *
 * **Why not `placementsFor`.** The journal's rule is that an ACCEPTED correction is not drawn at
 * all — "Accept rewrites the span and the underline does not return", which is true there because
 * the draft is rewritten under it. Nothing is rewritten here: the sentence in the bubble still
 * contains the mistake, and removing its underline because the learner tapped Accept would make
 * the app claim a sentence is correct while showing the learner that it is not. So in chat every
 * acted-on correction goes STALE — still marked, faded, no longer tappable — whatever the action
 * was. That is a difference in policy, not a difference in span arithmetic, which is why the span
 * arithmetic (`reanchor`, `dropOverlaps`) is still the shared implementation and only the
 * three-line rule about `learnerAction` is local.
 */
export function turnPlacements(
  turn: Pick<ConversationTurn, 'text' | 'corrections'>,
  acted: ReadonlyMap<string, CorrectionAction> = new Map(),
): Array<Placement<Correction>> {
  const corrections = turn.corrections ?? [];
  if (corrections.length === 0) return [];
  const placed: Array<Placement<Correction>> = [];
  for (const a of reanchor(corrections, turn.text)) {
    if (a.state !== 'anchored' || !a.span) continue;
    const action = acted.get(a.correction.id) ?? a.correction.learnerAction ?? null;
    placed.push({ correction: a.correction, span: a.span, stale: action !== null });
  }
  placed.sort((x, y) => x.span.charStart - y.span.charStart || x.span.charEnd - y.span.charEnd);
  // Two underlines on one word cannot both be tapped — the same reason the journal's mirror
  // de-overlaps, and the same function.
  return dropOverlaps(placed);
}

/** One piece of a bubble: a word to look up, a mark to tap, or plain text between them. */
export interface BubbleRun {
  /** rendered verbatim; the runs of a bubble rebuild its text exactly */
  text: string;
  /** present on a word — the gloss tap's payload, in the TURN's own coordinates */
  word?: { surface: string; charStart: number; charEnd: number };
  /** present when a correction covers this run — the tap opens the card instead of the gloss */
  correctionId?: string;
  /** the correction has been dealt with: still drawn, faded, no tap target */
  stale?: boolean;
}

const covers = (
  span: { charStart: number; charEnd: number },
  start: number,
  end: number,
): boolean => span.charStart < end && start < span.charEnd;

/**
 * A turn's text, cut into runs: `8e`'s two promises at once — *any word tappable → gloss* and
 * *corrections as soft underlines on your own bubble*.
 *
 * **The property that matters is the same one the journal's mirror has: the runs rebuild the text
 * exactly**, every character, once, in order. Here it matters for a different reason — the bubble
 * IS the text, so a lost character is a lost character in something the learner wrote — but the
 * test is the same one, and `culture.ts` has it too.
 *
 * Word tokens are split off their own punctuation (`tokenize` keeps `raw` and `word` apart), so a
 * trailing full stop is neither a tap target nor underlined. The tap target is the word exactly as
 * `wordTokens` would report it, which is what makes a gloss from a bubble the same request a gloss
 * from the reader is.
 *
 * A correction whose span covers only part of a word underlines the whole word. Splitting a word
 * to underline three of its letters was the alternative and it is wrong twice: it would make half
 * a Georgian word its own tap target, and a gloss of half a word is meaningless. Georgian
 * mistakes are overwhelmingly in the ending, so "the whole word is marked" is also what the
 * learner would draw themselves.
 */
export function bubbleRuns(text: string, placements: readonly Placement[] = []): BubbleRun[] {
  const runs: BubbleRun[] = [];
  const mark = (run: BubbleRun, start: number, end: number): BubbleRun => {
    const hit = placements.find((p) => covers(p.span, start, end));
    if (!hit) return run;
    run.correctionId = hit.correction.id;
    if (hit.stale) run.stale = true;
    return run;
  };

  for (const t of tokenize(text)) {
    if (!t.word) {
      runs.push(mark({ text: t.raw }, t.charStart, t.charEnd));
      continue;
    }
    // `WORD_TRIM` only strips runs from the two ends, so the word is a contiguous slice of `raw`
    // and these three pieces are the whole token — the same arithmetic `wordTokens` does.
    const inner = t.raw.indexOf(t.word);
    const at = t.charStart + (inner === -1 ? 0 : inner);
    if (at > t.charStart) {
      runs.push(mark({ text: text.slice(t.charStart, at) }, t.charStart, at));
    }
    const end = at + t.word.length;
    runs.push(
      mark({ text: t.word, word: { surface: t.word, charStart: at, charEnd: end } }, at, end),
    );
    if (end < t.charEnd) {
      runs.push(mark({ text: text.slice(end, t.charEnd) }, end, t.charEnd));
    }
  }
  return runs;
}

// ---------------------------------------------------------------------------------------------
// the recap
// ---------------------------------------------------------------------------------------------

/**
 * "4 min" — the recap's one number that is not a count.
 *
 * Coarse and locale-free for `editedAgo`'s reasons: the exact seconds of a conversation are not
 * information, and a locale-formatted duration would make this test depend on the machine's
 * timezone. `null` is a real answer — `conversation.duration_ms` is nullable — and it prints
 * nothing rather than "0 min", because a conversation that has no measured length did not take
 * no time.
 */
export function formatDuration(ms: number | null): string | null {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return 'under a minute';
  return `${mins} min`;
}

/**
 * The recap's headline, in `7a`'s register: a report, numbers without fanfare.
 *
 * Singular and plural are handled because "1 turns" is the kind of detail that makes a screen
 * read as unfinished, and this screen's whole job is to be believed.
 */
export function recapHeadline(recap: Pick<ConversationRecap, 'turns' | 'durationMs'>): string {
  const turns = `${recap.turns} ${recap.turns === 1 ? 'turn' : 'turns'}`;
  const took = formatDuration(recap.durationMs);
  return took ? `${turns} · ${took}` : turns;
}

// ---------------------------------------------------------------------------------------------
// what the screen says when something refuses
// ---------------------------------------------------------------------------------------------

/**
 * A failed call → one calm line.
 *
 * `screens/Journal.tsx` has a `calmLine` of its own with the same shape and it is NOT exported;
 * this is not that function ported, it is the same idea with chat's own three refusals, which are
 * genuinely different sentences: a conversation that has run long (429 on the turn cap), a locked
 * spoken mode (403 from `Me.capabilities`) and a spent budget. The duplication that remains — the
 * `detail`-then-`errors[0]`-then-fallback ladder — is four lines, and lifting it into a shared
 * module is worth doing when a third screen needs it. Reported rather than done here: neither
 * `Journal.tsx` nor a new shared module is this step's to write.
 *
 * Every line says what is still true. A learner whose turn was refused has not lost the
 * conversation, and the copy says so rather than apologising.
 */
export function chatCalmLine(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const { status, detail } = err.problem;
    if (detail) return detail;
    if (status === 403) return 'Text chat is part of the paid layer.';
    if (status === 429) return 'The AI helper is resting for a bit — the conversation is saved.';
    const field = err.problem.errors?.[0]?.message;
    if (status === 422 && field) return field;
  }
  return fallback;
}

// ---------------------------------------------------------------------------------------------
// the API surface this screen needs
// ---------------------------------------------------------------------------------------------

/**
 * The five methods `lib/api.ts` gains for chat, plus the one it already has.
 *
 * `api.ts` belongs to the integrator, so the screen is written against this interface and the
 * integrator adds methods matching it — the same arrangement `JournalApi` records, and the same
 * `satisfies`-style check at the top of the screen. Declared here rather than inside the screen so
 * the exact shape is one grep away from the routes it mirrors.
 *
 * `correctionAct` is the journal's method verbatim and is NOT re-declared with a chat-shaped name:
 * `/corrections/:id/act` is one route for both surfaces, and `routes/compositions.ts` says in its
 * own comment that this is why it lives where it does.
 */
export interface ChatApi {
  conversationList(q?: { limit?: number; cursor?: string }): Promise<Page<ConversationSummary>>;
  conversationStart(body: ConversationStartRequest): Promise<Conversation>;
  conversationGet(conversationId: string): Promise<Conversation>;
  conversationTurn(
    conversationId: string,
    body: ConversationTurnRequest,
  ): Promise<ConversationTurnResponse>;
  conversationEnd(conversationId: string): Promise<Conversation & { recap: ConversationRecap }>;
  correctionAct(correctionId: string, body: CorrectionActionRequest): Promise<void>;
}
